import {
  Observable,
  Subject,
  ReplaySubject,
  BehaviorSubject,
  of,
  VirtualTimeScheduler,
  timer,
  combineLatest
} from "rxjs";
import {
  observeOn,
  map,
  reduce,
  mergeAll,
  distinctUntilChanged,
  switchMap,
  share,
  takeUntil,
  debounceTime,
  timestamp
} from "rxjs/operators";
import { curry, last, assoc, clamp } from "@zyzgroup/core-common";
import { useHashChange$ } from "@zyzgroup/core-web";
import {
  examples,
  TimelineInputArray,
  TimelineInput,
  MarbleInputArray,
  Example,
  Props,
  Timeline,
  Marble,
  EndMarker
} from "./data";
import { MAX_TIME } from "./styles";

// operator name
export const store$: Observable<string> = useHashChange$().pipe(
  map((route) => {
    let r = "merge";
    if (route && route.split("/rx/")[1]) {
      r = route.split("/rx/")[1];
    }
    return r;
  })
);

// operator 示例
export const example$: Observable<Example> = store$.pipe(
  map((route) => examples[route]),
  share({
    connector: () => new ReplaySubject(1),
    resetOnError: false,
    resetOnComplete: false,
    resetOnRefCountZero: false
  })
);

// input timelines
export const inputTimelines$: Observable<Timeline[]> = example$.pipe(
  switchMap((example) => of(inputsToTimelines(example.inputs))),
  share({
    connector: () => new ReplaySubject(1),
    resetOnError: false,
    resetOnComplete: false,
    resetOnRefCountZero: false
  })
);

// 移动的 Marble Subject
export const changedMarbleSubject$ = new BehaviorSubject<{
  marbleID?: number;
  time?: number;
}>({});

// 移动的 Marble
const changedMarble$ = changedMarbleSubject$.pipe(
  debounceTime(100),
  distinctUntilChanged()
);

// 移动的 EndMarker Subject
export const changedEndMarkerSubject$ = new BehaviorSubject<{
  timelineID?: number;
  time?: number;
}>({});

// 移动的 EndMarker
const changedEndMarker$ = changedEndMarkerSubject$.pipe(
  debounceTime(100),
  distinctUntilChanged()
);

// EndMarker 的最终time
export const endMarkerMinTime$ = combineLatest([
  inputTimelines$,
  changedEndMarker$
]).pipe(
  map(([inputTimelines, changedEndMarker]) => {
    let time = changedEndMarker.time ?? 0;
    for (const tl of inputTimelines) {
      time = Math.max(time, tl.end.time);
    }
    return time;
  })
);

export const outputTimeline$: Observable<Timeline> = combineLatest([
  inputTimelines$,
  example$,
  changedMarble$,
  changedEndMarker$,
  endMarkerMinTime$
]).pipe(
  debounceTime(100),
  map(
    ([
      inputTimelines,
      example,
      changedMarble,
      changedEndMarker,
      endMarkerMinTime
    ]) => {
      for (const tl of inputTimelines) {
        const marbleIndex = tl.marbles.findIndex(
          (m) => m.marbleID == changedMarble.marbleID
        );
        // 更改 移动的 marble 的 time
        if (marbleIndex >= 0 && changedMarble.time) {
          tl.marbles[marbleIndex].time = changedMarble.time;
        }
        // 更改 EndMarker 的 time
        if (
          tl.timelineID == changedEndMarker.timelineID &&
          changedEndMarker.time
        ) {
          tl.end.time = changedEndMarker.time;
        }
      }
      // 生成 scheduler
      const vtScheduler = new VirtualTimeScheduler(undefined, MAX_TIME);
      const inputTimelineMarbles$Array: Observable<Marble>[] =
        inputTimelines.map((tl) => toVTStream(vtScheduler, tl));
      const outputline$: Observable<any> = example
        .apply(inputTimelineMarbles$Array, vtScheduler)
        .pipe(
          // add 0.01 or else things at exactly MAX_TIME will cut off
          takeUntil(timer(MAX_TIME + 0.01, vtScheduler))
        );
      return formatOutputTimeline$(vtScheduler, outputline$, endMarkerMinTime);
    }
  ),
  mergeAll(),
  share({
    connector: () => new ReplaySubject(1),
    resetOnError: false,
    resetOnComplete: false,
    resetOnRefCountZero: false
  })
);

function formatOutputTimeline$(
  scheduler: VirtualTimeScheduler,
  stream: Observable<any>,
  endMarkerMinTime: number
): Observable<Timeline> {
  const subject$ = new ReplaySubject<Marble[]>(1);
  const stop$ = new Subject<void>();
  let endTime: number;
  stream
    .pipe(
      observeOn(scheduler),
      timestamp(scheduler),
      map(({ value, timestamp }) => {
        const marble =
          typeof value !== "object"
            ? {
                marbleID: calculateNotificationContentHash(value),
                content: value
              }
            : value;
        return assoc("time", (timestamp / MAX_TIME) * 100, marble);
      }),
      takeUntil(stop$),
      reduce((a, b) => a.concat(b), [])
      // map((items) =>
      //   items.map(
      //     (item, i) =>
      //       Object.assign(item, {
      //         // itemId: i
      //       }) as Marble
      //   )
      // )
    )
    .subscribe({
      next: (items) => {
        subject$.next(items);
      },
      error: undefined,
      complete: () => {
        endTime = scheduler.now();
      }
    });
  scheduler.flush();
  stop$.next();
  return subject$.pipe(
    map((marbles) => ({
      timelineID: 0,
      marbles,
      end: { time: clamp(endTime, endMarkerMinTime, 100) },
      interactive: false
    }))
  );
}

const toVTStream = curry(function (scheduler, data: Timeline) {
  return new Observable<Marble>((observer) => {
    data.marbles.forEach((item) =>
      scheduler.schedule(() => observer.next(item), item.time)
    );
  }).pipe(takeUntil(timer(data.end.time + 1, scheduler)));
});

function inputsToTimelines(inputs: TimelineInputArray): Timeline[] {
  return inputs.map((input, index) => ({
    timelineID: index,
    marbles: inputToMarbles(getInput(input)),
    end: { time: getTime(input) },
    interactive: true
  }));
}

function getInput(input: TimelineInput): MarbleInputArray {
  const lastInput = last(input);
  return (
    typeof lastInput === "number" ? input.slice(0, -1) : input
  ) as MarbleInputArray;
}

function getTime(input: TimelineInput) {
  const lastInput = last(input);
  return typeof lastInput === "number" ? lastInput : 100;
}

function inputToMarbles(stream: MarbleInputArray): Marble[] {
  return stream.map(({ t: time, c: content }) => ({
    marbleID: calculateNotificationHash({ time, content }),
    time,
    content
  }));
}

function calculateNotificationContentHash(content: string | number | boolean) {
  const SMALL_PRIME_1 = 59;
  const SMALL_PRIME_2 = 97;
  const SOME_PRIME_NUMBER = 877;
  if (typeof content === "string") {
    return content
      .split("")
      .map((x) => x.charCodeAt(0))
      .reduce((x, y) => x * SMALL_PRIME_1 + y * SMALL_PRIME_2);
  } else if (typeof content === "number") {
    return content * SOME_PRIME_NUMBER;
  } else {
    return content ? SOME_PRIME_NUMBER : SOME_PRIME_NUMBER * 3;
  }
}

function calculateNotificationHash({
  time,
  content
}: {
  time: number;
  content: string | number | boolean;
}) {
  const SMALL_PRIME = 7;
  const LARGE_PRIME = 1046527;
  const MAX = 100000;
  const contentHash = calculateNotificationContentHash(content);
  return ((time + contentHash + SMALL_PRIME) * LARGE_PRIME) % MAX;
}

export function getPercentageFn(parentElement: HTMLElement) {
  const { width, left } = parentElement.getBoundingClientRect();
  const ratio = 100 / width || 0.15;
  const elementLeft = left + window.scrollX;
  return (x: number) => (x - elementLeft) * ratio;
}

// export function getTimeChange$(element: HTMLElement) {
//   const timeChange$: Observable<number> = mouseDown$(element).pipe(
//     map((e) => (e.currentTarget as HTMLElement)!.parentElement!),
//     map(getPercentageFn),
//     switchMap((getPercentage: (x: number) => number) =>
//       documentMouseMove$.pipe(
//         takeUntil(documentMouseUp$),
//         map((e) => e.pageX),
//         map(getPercentage),
//         distinctUntilChanged()
//       )
//     )
//   );
//   return timeChange$;
// }

// export function getTime$(
//   props$: Observable<Props>,
//   timeSource$: Observable<number>,
//   timeChange$: Observable<number>,
//   isDraggable$: Observable<boolean>
// ): Observable<number> {
//   const restrictedTimeChange$: Observable<number> = pausable$<number>(
//     isDraggable$,
//     timeChange$
//   ).pipe(map((n) => (n >= 100 ? 100 : n <= 0 ? 0 : n)));
//   const minChange$: Observable<number> = props$.pipe(
//     map((p) => p.minTime),
//     distinctUntilChanged(),
//     withLatestFrom(timeSource$),
//     map(([propsMinTime, sourceTime]) => Math.max(propsMinTime, sourceTime))
//   );
//   const maxChange$: Observable<number> = props$.pipe(
//     map((p) => p.maxTime),
//     distinctUntilChanged(),
//     withLatestFrom(timeSource$),
//     map(([propsMaxTime, sourceTime]) => Math.min(propsMaxTime, sourceTime))
//   );
//   return merge(
//     // order matters
//     timeSource$,
//     restrictedTimeChange$,
//     minChange$,
//     maxChange$
//   ).pipe(
//     distinctUntilChanged(),
//     share({
//       connector: () => new ReplaySubject(1),
//       resetOnError: false,
//       resetOnComplete: false,
//       resetOnRefCountZero: false
//     })
//   );
// }
